import eventbus from '@app/eventbus'
import string from '@lib/string';

/**
 * A comman scenario: 
 * - A datatable is shown in a tab of a dialog. 
 * - An item in the datatable is opened in a dialog, or a new item is created. 
 * - The opened item is saved, a sub-item is saved, a sub-item is removed, created, whatever. 
 * - Multiple save actions can take place while the dialog is open. 
 * 
 * Now we want an easy way to catch the changes but we want: 
 * - we want to update the datatable just once. And after the dialog closes. 
 * 
 * Of course, a datatable is just an example. Can be anything that needs to be updated after a dialog is closed.
 * 
 * Multiple ways of doing this have been created in the past, each with quirks, issues and pitfalls. 
 * The purpose of this helper is to have a single, well-known way of doing this.
 * 
 *********************************************************************************************************************
 * Minimal example: 
 * 
 * import clsDialogUpdateHelper from '@shared/lib/clsDialogUpdateHelper';
 * 
 * let objRefresher = null;
 * onMounted(() => {
 *   objRefresher = new clsDialogUpdateHelper({
 *      closeModel: ['workorder'], 
 *      onCloseRefresh: (modelName, modelData) => {
 *      dt.refreshRows({id: modelData.id});
 *    }
 * })
 * onUnmounted(() => {
 *   objRefresher && objRefresher.cleanUp();
 * })
 * 
 *********************************************************************************************************************
 * 
 * More complex Example: 
 * 
 * let objRefresher = null;
 * function onCloseRefresh(modelName, modelData) {
 * 
 *    if (modelName == 'hourline') {
 *       refreshTotalsOnPage();
 *    } else if (modelName == 'hourregistration') {
 *       refreshOtherDetailsOnPage();
 *    }
 *    dt.refreshRows({id: modelData.id});
 * }
 * 
 * onMounted(() => {
 *   objRefresher = new clsDialogUpdateHelper({
 *      closeModel: ['workorder'],
 *      saveModel: ['hourline', 'hourregistration', 'workorder'], 
 *      removeModel: [], // all
 *      onCloseRefresh: onCloseRefresh
 *    
 * })
 * onUnmounted(() => {
 *   objRefresher && objRefresher.cleanUp();
 * })
 * 
 ************************************************************************************************************************
 * IMPORTANT NOTE: 
 * Some dialogs are mounted in the main App. 
 * Those are dialogs which can be opened from many locations. 
 * You should consider this in the onCloseRefresh function!!! 
 * Do NOT just execute a refresh blindly but check if the dialog is still open and if the data is relevant for the dialog.
 * 
 * EXAMPLE:==> See TabDossierWorkorders.vue: 
 *     if (props.model.isDialogOpen) {
 *        dt.refreshRows({id: modelData.id});
 *     }  
 * 
 */



class clsDialogUpdateHelper {
    closeModel      = null;
    saveModel       = null;
    removeModel     = null;
    onCloseRefresh  = null;
    arrFnUnRegister = [];

    shouldTriggerRefresh = false;

    dbg = false;

    constructor({closeModel = [], saveModel = [], removeModel = [], onCloseRefresh}, dbg = false) {

        if (!onCloseRefresh) {
            console.error("No onCloseRefresh function provided for clsDialogUpdateHelper");
        }

        this.closeModel = closeModel;
        this.saveModel = saveModel;
        this.removeModel = removeModel;
        this.onCloseRefresh = onCloseRefresh;

        this.shouldTriggerRefresh = false;
        this.dbg = dbg;

        this.registerEventListeners();
    }

    log(...args) {
        if (this.dbg) {
            console.log('clsDialogUpdateHelper -->', ...args);
        }
    }

    registerEventListeners() {
        this._registerSaveEventListeners();
        this._registerRemoveEventListeners();
        this._registerCloseEventListeners();

    }

    cleanUp() {
        this.log('Cleaning up clsDialogUpdateHelper', (this.arrFnUnRegister||[]).length);
        (this.arrFnUnRegister||[]).forEach(fn => fn());
    }

    _registerSaveEventListeners() {
        if (!this.saveModel) {
            this.saveModel = []; // All saved while the dialog is open will trigger a refresh.
        }
        if (typeof this.saveModel == 'string') {
            this.saveModel = [this.saveModel];
        }        
        this.log('Registering for model.save event');
        this.arrFnUnRegister.push(
            eventbus.model.saved.on( (type, para) => {
                if (!this.saveModel || !this.saveModel?.length) {
                    this.shouldTriggerRefresh = true;
                    this.log('model.saved refresh: true', this.saveModel, type, para);
                } else if (string.isInRange(type, this.saveModel)) {
                    this.shouldTriggerRefresh = true;
                    this.log('model.saved refresh: true', this.saveModel, type, para);
                } else {
                    this.log('model.saved refresh: false', this.saveModel, type, para);
                }
            })
        );
    }

    _registerRemoveEventListeners() {
        if (!this.removeModel) {
            this.removeModel = []; // All removed while the dialog is open will trigger a refresh.
        }
        if (typeof this.removeModel == 'string') {
            this.removeModel = [this.removeModel];
        }        
        this.log('Registering for model.removed event');
        this.arrFnUnRegister.push(
            eventbus.model.removed.on( (type, para) => {
                if (!this.removeModel || !this.removeModel?.length) {
                    this.shouldTriggerRefresh = true;
                    this.log('model.removed refresh: true', this.removeModel, type, para);
                } else if (string.isInRange(type, this.removeModel)) {
                    this.log('model.removed refresh: true', this.removeModel, type, para);
                    this.shouldTriggerRefresh = true;
                } else {
                    this.log('model.removed refresh: false', this.removeModel, type, para);
                }
            })
        );
    }

    _registerCloseEventListeners() {
        if (!this.closeModel) {
            console.error("Close model(s) not defined for clsDialogUpdateHelper - no refresh will be triggered on dialog close.");
            return; 
        }
        if (typeof this.closeModel == 'string') {
            this.closeModel = [this.closeModel];
        }        
        this.log('Registering for model.closed event');
        this.arrFnUnRegister.push(
            eventbus.dialog.closed.on( (type, para) => {
                var isTargetModel = string.isInRange(type, this.closeModel);
                var shouldRefresh = this.shouldTriggerRefresh;
                this.log('model.dialog.closed', isTargetModel, shouldRefresh, type, para);
                
                if (isTargetModel) {
                    // Reset the trigger flag and execute the refresh function if provided. 
                    this.shouldTriggerRefresh = false;
                    if (shouldRefresh) {
                        if (this.onCloseRefresh) {
                            this.onCloseRefresh(type, para);
                        }
                    }
                }
            })
        );

    }
}

export default clsDialogUpdateHelper;